import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, shareReplay, tap, distinctUntilChanged, filter } from 'rxjs/operators';
import { ApiService } from './api.service';
import { PassDataService } from './pass-data/pass-data.service';
import { Service, GetServiceResponse } from '../models/service/service';

/**
 * Caches the services active for the current domain, keyed by ServiceType.Name
 * (lowercase). Default-deny: callers must await ensureLoaded() before deciding.
 */
@Injectable({ providedIn: 'root' })
export class ServiceEntitlementService {
  // Shared so the route guard and the 403 handler cannot drift apart.
  public static readonly CATALOG_ROUTE = 'main/service-catalog';
  public static readonly NOT_ACTIVE_MESSAGE = 'SNACK_BAR.SERVICE_NOT_ACTIVE';

  private readonly apiService = inject(ApiService);
  private readonly passDataService = inject(PassDataService);

  // null = never resolved for the current domain; [] = resolved, nothing active.
  private readonly activeTypeNames = new BehaviorSubject<string[] | null>(null);
  private readonly activeServices = new BehaviorSubject<Service[]>([]);
  private inFlight: Observable<string[]> | null = null;
  private currentDomainId: string | null = null;

  public get activeServiceTypeNames$(): Observable<string[]> {
    return this.activeTypeNames.pipe(map(v => v ?? []));
  }

  public get activeServices$(): Observable<Service[]> {
    return this.activeServices.asObservable();
  }

  public get isResolved(): boolean {
    return this.activeTypeNames.getValue() !== null;
  }

  constructor() {
    this.passDataService.currentDomain$
      .pipe(
        filter(domain => !!domain?.Id),
        map(domain => domain.Id),
        distinctUntilChanged()
      )
      .subscribe(domainId => {
        if (this.currentDomainId !== null && this.currentDomainId !== domainId) {
          this.reset();
        }
        this.currentDomainId = domainId;
      });
  }

  public ensureLoaded(): Observable<string[]> {
    const current = this.activeTypeNames.getValue();
    if (current !== null) {
      return of(current);
    }
    if (this.inFlight) {
      return this.inFlight;
    }
    this.inFlight = this.apiService.getServicesActive().pipe(
      map((res: void | GetServiceResponse) => {
        const items: Service[] | undefined = (res as GetServiceResponse)?.Items;

        // A failed call resolves to undefined: keep the previous snapshot rather
        // than locking every page over one dropped request.
        if (!items) {
          this.inFlight = null;
          return this.activeTypeNames.getValue() ?? [];
        }

        this.activeServices.next(items);
        const names = items
          .map(s => s?.ServiceType?.Name?.toLowerCase())
          .filter((n): n is string => !!n);
        this.activeTypeNames.next(names);
        return names;
      }),
      tap({ error: () => { this.inFlight = null; } }),
      shareReplay(1)
    );
    return this.inFlight;
  }

  public reload(): Observable<string[]> {
    this.reset();
    return this.ensureLoaded();
  }

  // Call after changing a domain's activations: open pages relock without a reload.
  public invalidateDomain(domainId: string): void {
    if (domainId && domainId === this.currentDomainId) {
      this.reload().subscribe();
    }
  }

  public isServiceTypeActive(serviceTypeName: string): boolean {
    if (!serviceTypeName) {
      return false;
    }
    return (this.activeTypeNames.getValue() ?? [])
      .includes(serviceTypeName.toLowerCase());
  }

  private reset(): void {
    this.inFlight = null;
    this.activeServices.next([]);
    this.activeTypeNames.next(null);
  }
}
